<template>
  <li class="ai-chat-row" :class="{ 'ai-chat-row--current': current }">
    <div class="ai-chat-row__main">
      <button class="ai-chat-row__open" type="button" :aria-current="current ? 'true' : null" :title="chat.title" @click="$emit('select')">
        <svg class="ai-chat-row__icon" :class="{ 'ai-chat-row__icon--pinned': chat.isPinned }" width="20" height="20" viewBox="0 0 24 24" fill="none" role="img" :aria-hidden="!chat.isPinned" :aria-label="chat.isPinned ? 'Pinned' : null" focusable="false">
          <path v-if="chat.isPinned" d="m8 3 8 0-1 6 4 4v2h-6v6l-2-3v-3H5v-2l4-4-1-6Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
          <template v-else>
            <path d="M20 11.5a8 8 0 0 1-8 8H4l1.6-3.4A8 8 0 1 1 20 11.5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
            <path d="M8 9h8M8 13h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </template>
        </svg>
        <span class="ai-chat-row__copy">
          <strong>{{ title(chat.title) }}</strong>
          <span class="ai-chat-row__meta">
            <span v-if="current" class="ai-chat-row__accent">Current</span>
            <span v-if="chat.draft || chat.pending">Draft</span>
            <time v-if="row.updated_at" :datetime="row.updated_at">{{ date(row.updated_at) }}</time>
            <span v-else>Not sent yet</span>
          </span>
        </span>
      </button>
      <details ref="menu" class="ai-chat-row__menu" @toggle="syncMenuListener" @keyup.esc.stop="closeMenu">
        <summary :aria-label="`Options for ${title(chat.title)}`" title="Chat options">•••</summary>
        <div class="ai-chat-row__actions">
          <button type="button" :disabled="busy" @click="rename">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
              <path d="m15 5 4 4M4 20l4-1L20 7a2.8 2.8 0 0 0-4-4L4 15v5Z" />
            </svg>
            <span>Rename</span>
          </button>
          <button type="button" :disabled="busy" @click="togglePin">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
              <path v-if="chat.isPinned" d="M2 2l20 20M9 3h7l-1 6 4 4M9 9l-4 4v2h10M12 15v6" />
              <path v-else d="M8 3h8l-1 6 4 4v2H5v-2l4-4-1-6ZM12 15v6" />
            </svg>
            <span>{{ chat.isPinned ? 'Unpin' : 'Pin to top' }}</span>
          </button>
        </div>
      </details>
    </div>
    <form v-if="editing" class="ai-chat-row__rename" @submit.prevent="saveTitle" @keydown.esc.stop.prevent="cancelRename" @keyup.esc.stop>
      <label :for="`ai-chat-title-${chat.id}`">Chat name</label>
      <input :id="`ai-chat-title-${chat.id}`" ref="titleInput" v-model="name" type="text" maxlength="120" required :disabled="busy">
      <div class="ai-chat-row__rename-actions">
        <AppButton design="new" theme="info" size="small" type="submit" :disabled="busy || !name.trim()">{{ busy ? 'Saving…' : 'Save' }}</AppButton>
        <AppButton design="new" theme="dark" size="small" type="button" :disabled="busy" @click.native="cancelRename">Cancel</AppButton>
      </div>
    </form>
    <p v-if="chat.preferencesError" class="ai-chat-row__error" role="alert">{{ chat.preferencesError }}</p>
  </li>
</template>

<script>
import { shortChatTitle } from './model'

export default {
  name: 'KnowledgeChatListItem',
  props: {
    row: { type: Object, required: true },
    chat: { type: Object, required: true },
    current: { type: Boolean, default: false },
    save: { type: Function, required: true }
  },
  data() { return { editing: false, name: '' } },
  computed: {
    busy() { return this.chat.preferencesBusy || this.chat.sending }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick, true)
  },
  methods: {
    title: shortChatTitle,
    date(value) { return new Date(value).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) },
    syncMenuListener() {
      if (this.$refs.menu && this.$refs.menu.open) {
        document.addEventListener('click', this.handleOutsideClick, true)
      } else {
        document.removeEventListener('click', this.handleOutsideClick, true)
      }
    },
    handleOutsideClick(event) {
      const menu = this.$refs.menu
      if (menu && !menu.contains(event.target)) this.closeMenu()
    },
    closeMenu() {
      if (this.$refs.menu) this.$refs.menu.open = false
      document.removeEventListener('click', this.handleOutsideClick, true)
    },
    rename() {
      this.closeMenu()
      this.name = this.chat.title
      this.editing = true
      this.$nextTick(() => { if (this.$refs.titleInput) this.$refs.titleInput.select() })
    },
    cancelRename() {
      if (this.busy) return
      this.editing = false
      this.$nextTick(() => { if (this.$refs.menu) this.$refs.menu.querySelector('summary').focus() })
    },
    async saveTitle() {
      if (!this.name.trim() || this.busy) return
      if (await this.save(this.chat, { title: this.name.trim() })) this.cancelRename()
    },
    async togglePin() {
      this.closeMenu()
      await this.save(this.chat, { is_pinned: !this.chat.isPinned })
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-chat-row { border: 1px solid $ui-line; border-radius: 12px; background: $ui-surface; color: $ui-ink; }
.ai-chat-row--current { border-color: rgba($ui-blue, .45); background: rgba($ui-blue, .05); }
.ai-chat-row__main { display: flex; align-items: flex-start; padding: 6px; gap: 2px; }
.ai-chat-row__open { display: flex; flex: 1; min-width: 0; align-items: flex-start; gap: 10px; text-align: left; padding: 8px 6px; border: 0; border-radius: 8px; background: transparent; color: inherit; cursor: pointer; }
.ai-chat-row__open:hover { background: rgba($ui-blue, .04); }
.ai-chat-row__open:focus-visible, summary:focus-visible, input:focus { outline: 2px solid $ui-blue; outline-offset: 2px; }
.ai-chat-row__icon { flex-shrink: 0; color: $ui-blue; margin-top: 1px; }
.ai-chat-row__icon--pinned { color: $ui-green; }
.ai-chat-row__copy { flex: 1; min-width: 0; }
.ai-chat-row__copy strong { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; overflow-wrap: anywhere; font-size: 13px; line-height: 1.5; font-weight: 600; }
.ai-chat-row__meta { display: flex; flex-wrap: wrap; gap: 6px 10px; margin-top: 6px; color: $ui-muted; font-size: 11px; line-height: 1.4; }
.ai-chat-row__accent { color: $ui-blue; font-weight: 600; }
.ai-chat-row__menu { position: relative; flex-shrink: 0; }
.ai-chat-row__menu summary { list-style: none; cursor: pointer; padding: 8px; border-radius: 6px; color: $ui-muted; font-size: 11px; letter-spacing: 1px; }
.ai-chat-row__menu summary::-webkit-details-marker { display: none; }
.ai-chat-row__menu summary:hover, .ai-chat-row__menu[open] summary { background: rgba($ui-blue, .08); color: $ui-blue; }
.ai-chat-row__actions { position: absolute; top: 100%; right: 0; z-index: 2; width: 156px; padding: 4px; border: 1px solid $ui-line; border-radius: 8px; background: $ui-surface; box-shadow: 0 4px 12px #17243a1a; }
.ai-chat-row__actions button { display: flex; align-items: center; gap: 10px; width: 100%; text-align: left; border: 0; border-radius: 5px; background: transparent; padding: 10px; color: $ui-ink; cursor: pointer; font-size: 12px; }
.ai-chat-row__actions svg { flex-shrink: 0; }
.ai-chat-row__actions button:hover { background: $ui-surface-soft; }
.ai-chat-row__actions button:disabled { opacity: .5; cursor: default; }
.ai-chat-row__rename { padding: 0 12px 12px; }
.ai-chat-row__rename label { display: block; margin-bottom: 6px; color: $ui-muted; font-size: 12px; }
.ai-chat-row__rename input { width: 100%; box-sizing: border-box; padding: 8px; border: 1px solid $ui-line; border-radius: 6px; font-size: 13px; }
.ai-chat-row__rename-actions { display: flex; gap: 8px; margin-top: 10px; }
.ai-chat-row__error { margin: 0; padding: 0 12px 12px; color: #a64b24; font-size: 12px; line-height: 1.5; }
</style>
